import { IUser, IUserLog, IUserLogGroup } from 'bets.oddprovider.lib.common.ts/models'
import { endOfDay, startOfDay } from 'date-fns'
import { ApiClientUtils } from 'fwork-jsts-common/src/apiClient'
import { FormItemStyled, FormStyled } from 'fwork.react.apptemplate'
import moment from 'moment'
import { FilterQuery } from 'mongoose'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from '../../apiClient/axios'
import { UsersApiClient } from '../../apiClient/users'
import { UsersLogsGroupsApiClient } from '../../apiClient/usersLogsGroups'
import { DEFAULTGAP } from '../../globals'
import { IUsersLogsState } from '../../redux/reducers/usersLogs.slice'
import { RootState } from '../../redux/store'
import { DateTimePickerExtComponent } from '../common/datePicker'
import { DropDownSearchComponent } from '../common/dropDownSearch'
import { SearchInputComponent } from '../common/searchInput'

interface LogsGetWhereComponentProps {
  userId?: number,
  onChange?: (filter: FilterQuery<IUserLog>) => void,
  onSearchClick?: () => void,
}

interface SearchOptionsProps {
  text?: string | undefined,
  userId?: number | undefined,
  logId?: number | undefined,
  dtIni?: Date | undefined,
  dtFin?: Date | undefined,
}

export const LogsGetWhereComponent = (props: LogsGetWhereComponentProps) => {
  const usersLogsState: IUsersLogsState = useSelector((state: RootState) => state.usersLogsState)

  const { enqueueSnackbar } = useSnackbar()
  const [searchOptions, setSearchOptions] = useState<SearchOptionsProps | undefined>({
    dtIni: new Date(usersLogsState.filter?.$and?.filter(and => and.date)[0]?.date?.$gte?.value || startOfDay(new Date())),
    dtFin: new Date(usersLogsState.filter?.$and?.filter(and => and.date)[0]?.date?.$lte?.value || endOfDay(new Date()))
  })

  useEffect(() => {
    if (props.onChange)
      props.onChange(getWhere())
  }, [searchOptions])

  const [gettingUsers, setGettingUsers] = useState(false)
  const getUsers = async (where?: FilterQuery<IUser> | undefined) => {
    if (gettingUsers) return
    setGettingUsers(true)

    try {
      const response = await new UsersApiClient(axios).get({
        where,
        sort: { name: 1 }
      })
      return response.data?.payload
    } catch (error) {
      enqueueSnackbar(ApiClientUtils.getErrorMessage(error), { variant: 'error' })
    } finally {
      setGettingUsers(false)
    }
  }

  const [gettingLogsGroups, setGettingLogsGroups] = useState(false)
  const getLogsGroups = async (where?: FilterQuery<IUserLogGroup> | undefined) => {
    if (gettingLogsGroups) return
    setGettingLogsGroups(true)

    try {
      const response = await new UsersLogsGroupsApiClient(axios).get({
        where,
        sort: { name: 1 }
      })
      return response.data?.payload
    } catch (error) {
      enqueueSnackbar(ApiClientUtils.getErrorMessage(error), { variant: 'error' })
    } finally {
      setGettingLogsGroups(false)
    }
  }

  const getWhere = () => {
    const where: FilterQuery<IUserLog> | undefined = {}

    if (searchOptions?.text) {
      where.$or = [
        {
          'description': { $regex: searchOptions?.text, $options: 'i' }
        },
        {
          'additional_description': { $regex: searchOptions?.text, $options: 'i' }
        },
      ]
    }

    if (props.userId || searchOptions?.userId || searchOptions?.dtIni || searchOptions?.dtFin || searchOptions?.logId) {
      where.$and = []

      if (props.userId || searchOptions?.userId) where.$and.push({ user_id: props.userId || searchOptions?.userId })
      if (searchOptions?.logId) where.$and.push({ user_log_group_id: searchOptions?.logId })

      const dtIni = searchOptions?.dtIni?.toISOString()
      const dtFin = searchOptions?.dtFin?.toISOString()
      if (dtIni) where.$and?.push({ date: { "$gte": { type: 'Date', value: dtIni } } })
      if (dtFin) where.$and?.push({ date: { "$lte": { type: 'Date', value: dtFin } } })
    }

    return where
  }

  return <>
    <SearchInputComponent
      style={{
        marginInline: DEFAULTGAP,
      }}
      label='Pesquisar por descrição ou descrição adicional'
      onChange={e => {
        setSearchOptions({
          ...searchOptions,
          text: e.target.value
        })
      }}
      onKeyUp={e => { if (e.key === "Enter" && props.onSearchClick) props.onSearchClick() }}
      onIconClick={props.onSearchClick}
    />

    <FormStyled gap={DEFAULTGAP} style={{ paddingBottom: 0 }}>
      {!props.userId ? <FormItemStyled gap={DEFAULTGAP}>
        <DropDownSearchComponent
          progress={gettingUsers}
          initInputValue={searchOptions?.userId?.toString() || ''}
          label="Usuários"
          modelLabelName="name"
          dataBuilder={async (typedText: string | undefined) => {
            if (typedText)
              return await getUsers({
                name: { $regex: typedText, $options: 'i' }
              })
          }}
          onSelectItem={(item: IUser) => {
            setSearchOptions({
              ...searchOptions,
              userId: item.id
            })
          }}
          onClear={() => setSearchOptions({
            ...searchOptions,
            userId: undefined
          })}
        />
      </FormItemStyled> : <></>}

      <FormItemStyled gap={DEFAULTGAP}>
        <DropDownSearchComponent
          progress={gettingLogsGroups}
          initInputValue={''}
          label="Classificação de log"
          modelLabelName="name"
          dataBuilder={async (typedText: string | undefined) => {
            if (typedText)
              return await getLogsGroups({
                name: { $regex: typedText, $options: 'i' }
              })
          }}
          onSelectItem={(item: IUserLogGroup) => {
            setSearchOptions({
              ...searchOptions,
              logId: item.id
            })

          }}
          onClear={() => setSearchOptions({
            ...searchOptions,
            logId: undefined
          })}
        />
      </FormItemStyled>

    </FormStyled>

    <FormStyled gap={DEFAULTGAP}>

      <FormItemStyled gap={DEFAULTGAP}>
        <DateTimePickerExtComponent
          ampm={false}
          label="Data Inicial"
          // to makes component empty, use null value. undefined not worked
          value={moment(searchOptions?.dtIni) || moment(usersLogsState.filter?.$and?.filter(and => and.date)[0]?.date?.$gte?.value) || null}
          onChange={(value: any) => {
            setSearchOptions({
              ...searchOptions,
              dtIni: value!
            })
          }}
        />
      </FormItemStyled>

      <FormItemStyled gap={DEFAULTGAP}>
        <DateTimePickerExtComponent
          ampm={false}
          label="Data Final"
          value={moment(searchOptions?.dtFin) || moment(usersLogsState.filter?.$and?.filter(and => and.date)[0]?.date?.$lte?.value) || null}
          onChange={(value: any) => {
            setSearchOptions({
              ...searchOptions,
              dtFin: value!
            })
          }}
        />
      </FormItemStyled>
    </FormStyled>
  </>
}